/**
 * Integration-system identifiers, kept in their own module rather than in the
 * constants barrel.
 *
 * The barrel pulls in the workflow-template model, entity-app constants and the
 * file-validation utils. getSubmittalIntegrationConfig lives in a module that
 * ProjectProvider imports, so importing the barrel from there would put that
 * whole subgraph behind almost every screen and every test that renders one.
 * Nothing here imports anything.
 *
 * Both values are re-exported from constants/index, which is where the rest of
 * the app already reaches for them.
 */

/**
 * The `system` value the OPC integration is registered under on
 * integration.system. Its siblings in that table are AUTODESK / PROCORE /
 * BIM360 / CMIC / EXTERNAL (see IntegrationType).
 *
 * Deliberately the ONLY OPC-specific string behind the schedule sync and the
 * integrations grid: if the backend registers OPC under a different name this
 * constant is the single edit. Until it matches, the sync button renders
 * disabled and explains why rather than falling back to another config — firing
 * that endpoint at the wrong project_config_id would kick off somebody else's
 * import.
 */
export const OPC_INTEGRATION_SYSTEM = "OPC";

/**
 * Systems that feed the schedule and nothing else.
 *
 * A project can carry one of these alongside its submittal integration, which
 * makes project_integration_configs a multi-row array with no guaranteed order —
 * the query that fetches it has no order_by. Every submittal-side reader has to
 * skip these rows rather than take `[0]`: OPC's properties answer OPC questions,
 * so reading them for a submittal question yields an undefined SYNC_INTERVAL, an
 * undefined DATA_SOURCE_NAME, and an INTEGRATION_METHOD describing OPC's own
 * transport rather than the submittal source's.
 *
 * Applied through getSubmittalIntegrationConfig — see that helper.
 */
export const SCHEDULE_ONLY_INTEGRATION_SYSTEMS = [OPC_INTEGRATION_SYSTEM];

export const OPC_DEFAULT_ENDPOINT_URL = "https://primavera-us2.oraclecloud.com";

export const INTEGRATION_MODULE = {
  SUBMITTALS: "Submittals",
  SCHEDULES: "Schedules"
} as const;

export function getIntegrationModule(system: string | undefined): string {
  if (system === OPC_INTEGRATION_SYSTEM) return INTEGRATION_MODULE.SCHEDULES;
  return INTEGRATION_MODULE.SUBMITTALS;
}
